import React, { useState, useEffect, useRef } from 'react';
import Input from '../input/index.jsx';
import ArrowNoStem from '../icons/arrowNoStem.jsx';

const Dropdown = ({
  search = true,
  onChange,
  children,
  useSelectedStyle = false,
  zIndex = 2000,
  label,
  style: overwriteStyle = {},
  dataTestId = null,
  selectLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const [searchString, setSearchString] = useState('');
  const [selectedStyle, setSelectedStyle] = useState({});
  const ref = useRef(null);

  useEffect(() => {
    const hasSelectLabel = selectLabel !== undefined;
    const defaultChild = children.find(({ props }) => props.defaultValue && !props.disabled);
    if (!defaultChild && hasSelectLabel) {
      setSelected(selectLabel);
      return;
    }

    const fallbackChild = children.find(({ props }) => !props.disabled);
    const selectedChild = defaultChild ? defaultChild : fallbackChild;

    setSelected(selectedChild.props.label);

    if (useSelectedStyle) {
      setSelectedStyle(selectedChild.props.style);
    }
  }, [children]);

  useEffect(() => {
    window.addEventListener('click', handleBackdropClick);
    return () => window.removeEventListener('click', handleBackdropClick);
  }, []);

  const handleBackdropClick = (e) => {
    if (!ref?.current?.contains(e.target)) {
      setOpen(false);
      setSearchString('');
    }
  };

  const onInputFocus = () => {
    setOpen(true);
  };

  const onItemClick = ({ value, label, style }) => {
    if (typeof onChange === 'function') {
      onChange(value);
    }

    if (useSelectedStyle) {
      setSelectedStyle(style);
    }

    setSelected(label);
    setSearchString('');
    setOpen(false);
  };

  const handleSearch = ({ target: { value } }) => {
    setSearchString(value);
  };

  if (!Array.isArray(children)) {
    children = [children];
  }

  if (searchString) {
    children = children.filter(({ props: { label = '' } }) => {
      return label.toLowerCase().includes(searchString.toLowerCase());
    });
  }

  const wrapperHeight = label ? 48 + 12 : 48;

  return (
    <div style={{ height: wrapperHeight, ...styles.wrapper, ...overwriteStyle }} ref={ref}>
      <div style={styles.inputContainer} data-test-id={dataTestId}>
        <Input
          label={label}
          value={selected}
          readOnly={!search}
          onFocus={onInputFocus}
          style={{ ...styles.input, ...selectedStyle }}
          onChange={handleSearch}
        />
        <ArrowNoStem
          style={styles.icon}
        />
      </div>
      {open && (
        <div style={{ ...styles.listContainer, zIndex }}>
          {children.map((child, i) => {
            return React.cloneElement(child, { key: i, index: i, onClick: onItemClick });
          })}
        </div>
      )}
    </div>
  );
};

const styles = {
  wrapper: {
    display: 'inline-block',
    position: 'relative',
  },
  inputContainer: {
    position: 'relative',
  },
  input: {
    width: 'auto',
    paddingRight: 36,
  },
  icon: {
    position: 'absolute',
    bottom: 18,
    right: 18,
    marginTop: -2,
  },
  listContainer: {
    border: '1px solid black',
    position: 'absolute',
    top: '100%',
    left: 0,
    width: '100%',
    maxHeight: '224px',
    overflowY: 'auto',
  },
};

export default Dropdown;
